import decodeObject from '@schibsted-nmp/search-frontend-utils/decodeObject.js';
import {
    QueryClient,
    QueryClientProvider,
    hydrate,
    type DehydratedState,
} from '@tanstack/react-query';
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { App } from './App';
import type { AppProps } from './AppProps';
import { RootElementProvider } from './components/RootElement';
import { loadAndActivateI18n } from './i18n';

const PODLET_NAME = 'search-pole-position-podlet';

const wrapper = document.querySelector(`${PODLET_NAME}-isolated`);

if (wrapper?.shadowRoot) {
    const containerEle = wrapper.shadowRoot.querySelector('#app');
    const propsEle = wrapper.shadowRoot.querySelector(
        `script[type="application/json"][data-props]`,
    );
    const reactQueryStateEle = wrapper.shadowRoot.querySelector(
        `script[type="application/json"][data-react-query-state]`,
    );

    if (containerEle && propsEle && reactQueryStateEle) {
        try {
            const props = decodeObject<AppProps>(propsEle.textContent ?? '{}');
            const reactQueryState = decodeObject<DehydratedState>(
                reactQueryStateEle.textContent ?? '{}',
            );

            loadAndActivateI18n(props.locale);

            hydrateRoot(
                containerEle,
                <RootElementProvider rootElement={containerEle as HTMLElement}>
                    <HydratedApp dehydratedState={reactQueryState} {...props} />
                </RootElementProvider>,
            );
        } catch (err) {
            throw new Error(
                `SSR(hydrate): Props (${
                    propsEle.textContent
                }) were provided but parse failed: Error message was: ${
                    (err as Error).message
                }`,
            );
        }
    }
}

type HydratedAppProps = AppProps & {
    dehydratedState: unknown;
};

function HydratedApp(props: HydratedAppProps) {
    const { dehydratedState, ...appProps } = props;

    const [queryClient] = React.useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        refetchOnWindowFocus: false,
                    },
                },
            }),
    );

    hydrate(queryClient, dehydratedState);

    return (
        <QueryClientProvider client={queryClient}>
            <App {...appProps} />
        </QueryClientProvider>
    );
}
